import { siteMeta } from '../data/siteMeta';
import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'childrenprivacy_welcomed_v2';

function FirstTimeModal() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      // Small delay so page renders first
      const timer = setTimeout(() => setIsOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
  }

  function scrollToHowItWorks() {
    dismiss();
    setTimeout(() => {
      const el = document.getElementById('how-it-works');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  if (!isOpen) return null;

  const highlights = [
    { icon: 'fa-landmark', color: 'text-red-500', label: 'Federal laws', note: 'KIDS Act, KOSA, COPPA 2.0, Take It Down' },
    { icon: 'fa-users', color: 'text-blue-500', label: 'Social media restrictions', note: '25 state laws' },
    { icon: 'fa-id-card', color: 'text-green-500', label: 'Age verification', note: '27 states' },
    { icon: 'fa-robot', color: 'text-yellow-500', label: 'AI chatbot safety', note: '15 states' },
    { icon: 'fa-code', color: 'text-indigo-500', label: 'Design codes (AADC)', note: '6 states' },
    { icon: 'fa-globe', color: 'text-teal-500', label: 'International laws', note: '18 jurisdictions' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={dismiss}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header bar — dark navy, no bright-on-bright clash */}
        <div className="p-6 text-white" style={{background: 'linear-gradient(135deg, #1e1b4b 0%, #4c1d95 100%)'}}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <i className="fas fa-shield-halved text-violet-300 text-lg"></i>
                <span className="text-xs font-semibold uppercase tracking-widest text-violet-300">2026 Edition</span>
              </div>
              <h2 className="text-xl font-bold text-white leading-snug">Children's Privacy Laws Tracker</h2>
              <p className="text-violet-200 text-sm mt-1">125+ laws · US federal &amp; state · 18 international jurisdictions · {siteMeta.updateLabel} ({siteMeta.updatedOn})</p>
            </div>
            <button onClick={dismiss} className="text-violet-300 hover:text-white ml-4 flex-shrink-0 transition-colors">
              <i className="fas fa-times text-lg"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 text-sm leading-relaxed mb-5">
            A free tool for <strong className="text-gray-800">compliance teams, legal professionals, parents, and policymakers</strong> tracking the fast-moving landscape of children's online privacy law — US federal, all 50 states, and international.
          </p>

          {/* What's covered */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">What's covered</p>
          <div className="grid grid-cols-2 gap-2 mb-5">
            {highlights.map((h, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg border border-slate-200">
                <i className={`fas ${h.icon} ${h.color} text-sm w-4 text-center`}></i>
                <div>
                  <div className="text-xs font-semibold text-gray-800 leading-tight">{h.label}</div>
                  <div className="text-xs text-gray-500 leading-tight">{h.note}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick tips */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 mb-5">
            <p className="text-xs font-semibold text-indigo-700 mb-2"><i className="fas fa-lightbulb mr-1"></i>Quick tips</p>
            <ul className="text-xs text-indigo-900 space-y-1">
              <li><i className="fas fa-check text-indigo-400 mr-2"></i>Use the <strong>tabs</strong> to filter by law category</li>
              <li><i className="fas fa-check text-indigo-400 mr-2"></i>Use the <strong>search bar</strong> to find any state, country, or law name</li>
              <li><i className="fas fa-check text-indigo-400 mr-2"></i><strong>Click any card</strong> to see full requirements and compliance notes</li>
              <li><i className="fas fa-check text-indigo-400 mr-2"></i>Switch between <strong>Cards / Table / Timeline</strong> views</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={dismiss}
              className="flex-1 text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition-opacity hover:opacity-90"
              style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}
            >
              <i className="fas fa-play mr-2"></i>Start Exploring
            </button>
            <button
              onClick={scrollToHowItWorks}
              className="flex-1 border border-slate-300 hover:border-slate-400 text-gray-700 hover:text-gray-900 font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm bg-slate-100"
            >
              <i className="fas fa-circle-question mr-2 text-indigo-500"></i>How It Works
            </button>
          </div>

          <p className="text-xs text-gray-400 text-center mt-4">For informational purposes only · Not legal advice</p>
        </div>
      </div>
    </div>
  );
}

export default FirstTimeModal;
